import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { goi } from './api';
import { NHIP_MS } from './hoi-lai';

/**
 * Chấm xanh / chấm đỏ.
 *
 * 🔴 Đã chốt: **mất mạng thì DỪNG, không chạy tiếp.** Tồn kho là sự thật nằm
 *    trên server — không có mạng thì không biết còn hàng hay không, và làm tiếp
 *    là hứa sai với khách.
 *
 *    Nên chấm đỏ không phải để trang trí: nó **khoá mọi nút hành động** và hiện
 *    một dải cảnh báo. Màn hình giữ nguyên để bấm lại được khi mạng về — đã chốt
 *    ở lượt 2, không tự tải lại làm mất thứ đang gõ dở.
 */

interface TrangThai {
  song: boolean;
  soLanHong: number;
  ngayLamViec: string | null;
}

const Ngu = createContext<TrangThai>({ song: true, soLanHong: 0, ngayLamViec: null });

export const useKetNoi = () => useContext(Ngu);

/** Một lần trượt chưa báo động — wifi cửa hàng hay nấc. Hai lần thì có chuyện. */
const NGUONG_HONG = 2;

export function NhaKetNoi({ children }: { children: React.ReactNode }) {
  const [tt, datTt] = useState<TrangThai>({ song: true, soLanHong: 0, ngayLamViec: null });
  const dangChay = useRef(false);

  useEffect(() => {
    let conSong = true;
    const hoi = async () => {
      if (dangChay.current) return;
      dangChay.current = true;
      try {
        const d = await goi<{ ok: boolean; ngay_lam_viec: string }>('/api/health');
        if (conSong) datTt({ song: d.ok, soLanHong: 0, ngayLamViec: d.ngay_lam_viec });
      } catch {
        if (conSong) {
          datTt((c) => ({
            ...c,
            soLanHong: c.soLanHong + 1,
            song: c.soLanHong + 1 < NGUONG_HONG,
          }));
        }
      } finally {
        dangChay.current = false;
      }
    };
    void hoi();
    const h = setInterval(hoi, NHIP_MS);
    return () => {
      conSong = false;
      clearInterval(h);
    };
  }, []);

  return <Ngu.Provider value={tt}>{children}</Ngu.Provider>;
}

/**
 * Chấm xanh thở rất nhẹ — đủ để biết màn hình còn sống mà không kéo mắt.
 * Chấm đỏ nhấp nhanh hơn hẳn: lúc đó nó PHẢI kéo mắt.
 */
export function ChamKetNoi() {
  const { song } = useKetNoi();
  return (
    <span
      title={song ? 'Đang kết nối máy chủ' : 'MẤT KẾT NỐI MÁY CHỦ'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 12,
        fontWeight: 600,
        color: song ? 'var(--chu-2)' : 'var(--nhan)',
        transition: 'color var(--vua)',
        flexShrink: 0,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: song ? 'var(--tot)' : 'var(--nhan)',
          animation: song ? 'tho 2.6s ease-in-out infinite' : 'nhap-nhay 1s ease-in-out infinite',
          flexShrink: 0,
        }}
      />
      {/*
        Màn hẹp thì giấu CHỮ, giữ CHẤM. Chấm mới là thứ phải nhìn thấy — và lúc
        mất kết nối còn có dải cảnh báo đỏ chắn ngang đầu màn hình, nên không
        mất thông tin gì.
      */}
      <span className={song ? 'an-khi-hep' : undefined}>
        {song ? 'Kết nối' : 'Mất kết nối'}
      </span>
    </span>
  );
}

/** Dải cảnh báo trượt xuống chắn ngang đầu màn hình khi mất mạng. */
export function DaiMatKetNoi() {
  const { song } = useKetNoi();
  if (song) return null;
  return (
    <div
      role="alert"
      className="hien-len"
      style={{
        background: 'linear-gradient(145deg, #f4225f, #cc003e)',
        color: '#fff',
        padding: '12px 18px',
        borderRadius: 'var(--bo-goc)',
        boxShadow: 'var(--noi-nhan)',
        fontWeight: 700,
        letterSpacing: '0.01em',
        textAlign: 'center',
        fontSize: 'var(--chu-nho)',
        flexShrink: 0,
      }}
    >
      MẤT KẾT NỐI MÁY CHỦ — đã tạm khoá thao tác. Màn hình giữ nguyên, mạng về là bấm lại được.
    </div>
  );
}
